/* eslint-disable react/display-name */

import { ApexOptions } from 'apexcharts'
import moment from 'moment'
import { FC, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { DOMContainer } from 'react-overlays/esm/useWaitForDOMRef'
import Overlay from 'react-overlays/Overlay'
import H4 from '../../components/h4'
import { usePatients } from '../../hooks/patients'
import { BLUE_PRINCIPAL } from '../../utils/colors'
import { PatientOverlayCard, PatientOverlayContainer } from '../examResult/style'
import { GraphContainer } from './style'

export type PatientOverlayProps = {
  patientId: string
  examId: string
  target?: DOMContainer<HTMLElement> | null
  onHide?: () => void
}

type ChartData = {
  category: string | Date
  value: number
}

export type ChartProps = {
  data: ChartData[]
  name: string
}

const chartConfig = {
  options: {
    colors: [BLUE_PRINCIPAL],
    fill: {
      colors: ['#190F55'],
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        stops: [0, 90, 100]
      }
    },
    chart: {
      height: 247,
      toolbar: false
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'straight',
      width: 2
    },
    xaxis: {
      type: 'datetime',
      categories: [
        '2018-09-19T00:00:00.000Z',
        '2018-09-19T01:30:00.000Z',
        '2018-09-19T02:30:00.000Z',
        '2018-09-19T03:30:00.000Z',
        '2018-09-19T04:30:00.000Z',
        '2018-09-19T05:30:00.000Z',
        '2018-09-19T06:30:00.000Z'
      ]
    },
    tooltip: {
      x: {
        format: 'dd/MM/yy'
      }
    }
  } as ApexOptions
}

const ApexChart: FC<ChartProps> = ({ data, name }): ReactElement => {
  const series = useMemo(() => {
    return [{
      name,
      data: data.map((d, i) => i)
    }]
  }, [data, name])

  const options = useMemo(() => {
    return {
      ...chartConfig.options,
      xaxis: {
        ...chartConfig.options.xaxis,
        categories: data.map(d => String(d.category))
      }
    } as ApexOptions
  }, [data])

  return (
    <div id='chart'>
      <ReactApexChart options={options} series={series} type='area' height={247} />
    </div>
  )
}

const ExamHistoryOverlay: FC<PatientOverlayProps> = ({
  onHide,
  target,
  patientId,
  examId
}): ReactElement | null => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { getExam, exams } = usePatients()

  useEffect(() => {
    if (patientId && examId) {
      setIsLoading(true)
      getExam(patientId, examId).finally(() => setIsLoading(false))
    }
  }, [getExam, setIsLoading])

  const examName = useMemo(() => {
    return Object.entries(exams)?.[0]?.[1]?.name
  }, [exams])

  const examsData = useMemo(() => {
    return Object.entries(exams).sort(function (a, b) {
      return (+moment(a[0], 'YYYY-MM-DD-HH-mm-ss').toDate()) - (+moment(b[0], 'YYYY-MM-DD-HH-mm-ss').toDate())
    }).map(e => ({
      category: moment(e[0], 'YYYY-MM-DD-HH-mm-ss').toDate(),
      value: e[1].result
    }))
  }, [exams])

  if (!target) return null

  console.log('ED', examsData)

  return (
    <PatientOverlayContainer ref={containerRef}>
      <Overlay
        show
        rootClose
        onHide={onHide}
        container={containerRef}
        target={target}
        placement='right'
        offset={[0, 10]}
      >
        {({ props }) => (
          <PatientOverlayCard {...props}>
            <GraphContainer>
              <H4>Histórico do paciente com {examName}</H4>
              {isLoading && 'Carregando...'}
              {!isLoading && (
                <ApexChart
                  key={patientId + examId}
                  data={examsData}
                  name={examName || ''}
                />
              )}
            </GraphContainer>
          </PatientOverlayCard>
        )}
      </Overlay>
    </PatientOverlayContainer>
  )
}

export default ExamHistoryOverlay
